import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddTicketParams,
    EditTicketParams, FilterParams,
    Ticket, TicketOptionData,
    TicketParams,
    TicketStatistics,
    TicketWithPage,
} from '@app/interfaces/lm/ticket';
import { environment } from '@env/environment';
import { SelectOption } from '@app/interfaces/sys/dict';
import { lastValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class TicketService {
    private moduleApi = environment.api.services.lm;
    private baseUrl = this.moduleApi + '/ticket';

    constructor(private http: HttpClient) {
    }

    async findMyTicket(params: TicketParams): Promise<TicketWithPage> {
        return lastValueFrom(this.http.get<TicketWithPage>(`${this.baseUrl}/list/my`, {
            params: Object.assign(params),
        }));
    }

    async getListOption(params: {
        status: string
    }): Promise<TicketOptionData> {
        return lastValueFrom(this.http.get<TicketOptionData>(`${this.baseUrl}/options`, {
            params: Object.assign(params),
        }));
    }

    async export(params: FilterParams): Promise<any> {
        let url = `${this.baseUrl}/list/my/export`;
        Object.keys(params).forEach((paramsKey, index) => {
            const value = params[paramsKey as keyof FilterParams];
            if (!value) {
               return;
            }
            url = `${url}${index === 0 ? '?' : '&'}${paramsKey}=${value}`;
        });
        return this.http.post<any>(url, {}, {
            responseType: 'blob' as 'json',
            observe: 'response',
            headers: {
                resultIsFile: 'true',
            },
        }).subscribe((response) => {
            this.downloadFile(response);
        });
    }

    downloadFile(response: HttpResponse<any>): void {
        let filename = '';
        let contentType = '';
        const disposition = response.headers.get('content-disposition');
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        contentType = response.headers.get('content-type') as string;
        console.log('contentType', contentType);
        const blob = new Blob([response.body], { type: contentType }); // 创建 Blob 对象
        const URL = window.URL || window.webkitURL;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        }, 100);
    }

    exportFile(resource: string, csvData: string): void {
        const now = new Date();
        const currentDateTime = now.toISOString().replace(/[^\d]/g, '').slice(0, 14);

        let filename = `${resource.toLowerCase()}_${currentDateTime}.csv`;
        let contentType = 'test/csv';

        const blob = new Blob([csvData], { type: contentType });
        const URL = window.URL || window.webkitURL;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
        }, 500);
    }

    dataURLtoBlob(base64result: string, filename: string): void {
        const raw = window.atob(base64result);
        const uInt8Array = new Uint8Array(base64result.length);
        for (let i = 0; i < raw.length; i++) {
            uInt8Array[i] = raw.charCodeAt(i);
        }
        const link = document.createElement('a');
        const blob = new Blob([uInt8Array], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        link.href = URL.createObjectURL(blob);
        link.style.visibility = 'hidden';
        link.setAttribute('target', '_blank');
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async one(params: { ticketId: string }): Promise<Ticket> {
        return lastValueFrom(this.http.get<Ticket>(`${this.baseUrl}/list/by/id`, {
            params: Object.assign(params),
        }));
    }

    async statistics(): Promise<TicketStatistics> {
        return lastValueFrom(this.http.get<TicketStatistics>(`${this.baseUrl}/list/my/statistics`));
    }

    async findProjectTicket(params: { projectId: string }): Promise<Ticket[]> {
        return lastValueFrom(this.http.get<Ticket[]>(`${this.baseUrl}/list/by/project`, {
            params: Object.assign(params),
        }));
    }

    async findTaskTicket(params: { taskId: string }): Promise<Ticket[]> {
        return lastValueFrom(this.http.get<Ticket[]>(`${this.baseUrl}/list/by/task`, {
            params: Object.assign(params),
        }));
    }

    async addTicket(params: AddTicketParams): Promise<Ticket> {
        return lastValueFrom(this.http.post<Ticket>(`${this.baseUrl}/create`, params));
    }

    async editTicketInformation(params: EditTicketParams): Promise<Ticket> {
        return lastValueFrom(this.http.post<Ticket>(`${this.baseUrl}/edit/information`, params));
    }

    async fetchSeverityOptions(): Promise<SelectOption[]> {
        return new Promise(resolve => {
            const data: SelectOption[] = [
                {
                    label: 'Critical',
                    value: 'CRITICAL',
                },
                {
                    label: 'Major',
                    value: 'MAJOR',
                },
                {
                    label: 'Medium',
                    value: 'MEDIUM',
                },
                {
                    label: 'Low',
                    value: 'LOW',
                },
            ];
            resolve(data);
        });
    }

    formatSeverity(inputStr: string): string {
        var ret: string;
        switch (inputStr) {
            case 'CRITICAL': {
                ret = 'Critical';
                break;
            }
            case 'MAJOR': {
                ret = 'Major';
                break;
            }
            case 'MEDIUM': {
                ret = 'Medium';
                break;
            }
            case 'LOW': {
                ret = 'Low';
                break;
            }
            default: {
                ret = inputStr;
                break;
            }
        }
        return ret;
    }

}
